import PropTypes from 'prop-types';
import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import { Box, Link, Drawer, Typography, Avatar, Stack, Chip } from '@mui/material';
import { profile } from '../../../api/auth';
import useResponsive from '../../../hooks/useResponsive';
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import navConfig from './config';
import { useNotifications } from '../../../contexts/NotificationContext';

// ----------------------------------------------------------------------

const NAV_WIDTH = 280;

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 2,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
}));

// ── Live unread badge rendered inside the Notification Center nav item ────────

function UnreadBadge({ count }) {
  if (!count) return null;
  return (
    <Chip
      label={count > 99 ? '99+' : String(count)}
      size="small"
      color="error"
      sx={{
        height: 18,
        fontSize: '0.65rem',
        fontWeight: 700,
        minWidth: 18,
        ml: 0.5,
        '& .MuiChip-label': { px: 0.625 },
      }}
    />
  );
}

UnreadBadge.propTypes = { count: PropTypes.number };

// ── Inject live badges into the static navConfig ──────────────────────────────
// Walks the config and replaces `badgeKey: 'notificationUnread'` with a rendered chip.

function buildNavWithBadges(config, badgeMap) {
  return config.map((section) => ({
    ...section,
    items: (section.items || []).map((item) => {
      if (!item.badgeKey || !badgeMap[item.badgeKey]) return item;
      return { ...item, badge: badgeMap[item.badgeKey] };
    }),
  }));
}

// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav:    PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const { pathname }  = useLocation();
  const [account, setAccount] = useState({});
  const { unread }    = useNotifications();
  const isDesktop     = useResponsive('up', 'lg');

  useEffect(() => {
    profile().then(setAccount).catch(() => {});
  }, []);

  useEffect(() => {
    if (openNav) onCloseNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Re-build the config only when the unread count changes
  const navWithBadges = useMemo(
    () => buildNavWithBadges(navConfig, { notificationUnread: <UnreadBadge count={unread} /> }),
    [unread]
  );

  const renderContent = (
    <Scrollbar sx={{ height: 1, '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ px: 2.5, py: 3, display: 'inline-flex' }}>
        <Logo />
      </Box>

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <Link underline="none">
          <StyledAccount>
            <Avatar src={account.photoURL} alt="photoURL" />
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                {account.displayName}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {account.role}
              </Typography>
            </Box>
          </StyledAccount>
        </Link>
      </Box>

      <NavSection data={navWithBadges} />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box component="nav" sx={{ flexShrink: { lg: 0 }, width: { lg: NAV_WIDTH } }}>
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: { width: NAV_WIDTH, bgcolor: 'background.paper', borderRightStyle: 'solid', borderRightColor: 'divider' },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: NAV_WIDTH } }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
